import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { ReviewAnnotateForm } from '@/components/admin/sessions/review/review-annotate-form';
import type { CaseDrop } from '@/components/admin/sessions/review/review-case';
import { ReviewProvider } from '@/components/admin/sessions/review/review-context';
import { ReviewHead } from '@/components/admin/sessions/review/review-head';
import { ReviewLegend } from '@/components/admin/sessions/review/review-legend';
import { ReviewPanel, type ReviewTab } from '@/components/admin/sessions/review/review-panel';
import { ReviewPlacedList } from '@/components/admin/sessions/review/review-placed-list';
import {
  type AnchorSource,
  ReviewPopover,
} from '@/components/admin/sessions/review/review-popover';
import { ReviewRangeList } from '@/components/admin/sessions/review/review-range-list';
import { ReviewTranscript } from '@/components/admin/sessions/review/review-transcript';
import { type PassageTarget, rangeOperation } from '@/lib/review-gestures';
import {
  INELIGIBLE_SENTENCE,
  REVIEW_FAILURE_SENTENCE,
  REVIEW_REFUSAL_SENTENCE,
  reviewErrorSentence,
  reviewRefusalOf,
} from '@/lib/review-labels';
import type { SelectionRead } from '@/lib/review-selection';
import {
  PERSON_SPEAKER,
  SELECTION_REFUSAL,
  interviewerName,
  personFirstName,
  reviewTurnsOf,
} from '@/lib/review-transcript';
import {
  REVIEW_EVENTS_KEY,
  REVIEW_KEY,
  fetchReviewEvents,
  postReviewOperations,
} from '@/lib/session-review-api';
import { cn } from '@/lib/utils';
import {
  type PersonaPresentation,
  type ReviewOperation,
  type ReviewOperationsBody,
  type SessionDetail,
  type SessionReview,
  reviewOperationsBodySchema,
} from '@matter_extractor/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * The page of the review, assembled (relecture.md §4 to §10): the head, the
 * transcript with its marks, and the panel with its four tabs.
 *
 * ## Every gesture is saved at once
 *
 * There is no « Enregistrer » (§4.4): a gesture is sent as it is made, each in its
 * own transaction with its journal line, and the answer - the review AFTER it -
 * replaces what the page holds. The last gesture on an element wins, and the page
 * reads the review again when it gets the focus back (the route's query): no
 * channel in real time.
 *
 * ## What it holds, and what it does not
 *
 * The state of the PAGE - the tab open, the review folded, the popover, the case
 * an arrival lights, the selection being dragged. The review itself lives in the
 * query cache: this component never copies it.
 *
 * ## Where the screen stops
 *
 * A session that cannot be reviewed - still in progress, or without a protocol
 * page - gets the sentence that says so and the way back, never a transcript: the
 * first is still moving and the second has nothing to range.
 */

type Popover =
  | { readonly kind: 'range'; readonly target: PassageTarget; readonly anchor: AnchorSource }
  | { readonly kind: 'placed'; readonly ids: readonly string[]; readonly anchor: AnchorSource }
  | {
      readonly kind: 'annotate';
      readonly turn: number;
      readonly passage: { start: number; end: number; text: string } | null;
      readonly anchor: AnchorSource;
    }
  | { readonly kind: 'refusal'; readonly anchor: AnchorSource };

type Notice = { readonly text: string; readonly code: string | null };

/** How long a case stays lit after the name of it was pressed. */
const FLASH_MS = 1_900;

export interface ReviewScreenProps {
  readonly detail: SessionDetail;
  readonly review: SessionReview;
  /** How the persona introduces itself, or null when no version reads: it speaks as « IA ». */
  readonly presentation: PersonaPresentation | null;
  /** The signed-in operator, which keys the caches. */
  readonly email: string | null;
  /** What the route puts above the page - the banner of the trash. */
  readonly banner?: ReactNode;
}

export function ReviewScreen({ detail, review, presentation, email, banner }: ReviewScreenProps) {
  const client = useQueryClient();
  const sessionId = detail.session.id;
  const { eligibility, grid } = review;
  const turns = useMemo(() => reviewTurnsOf(detail.turns), [detail.turns]);

  const [tab, setTab] = useState<ReviewTab>('rangement');
  const [folded, setFolded] = useState(false);
  const [popover, setPopover] = useState<Popover | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [flashCase, setFlashCase] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [over, setOver] = useState<string | null>(null);
  const dragged = useRef<PassageTarget | null>(null);
  const flashTimer = useRef<number | undefined>(undefined);

  const events = useQuery({
    queryKey: REVIEW_EVENTS_KEY(email, sessionId),
    queryFn: () => fetchReviewEvents(sessionId),
    retry: false,
  });

  const write = useMutation({
    mutationFn: (body: ReviewOperationsBody) => postReviewOperations(sessionId, body),
    onSuccess: (result) => {
      client.setQueryData(REVIEW_KEY(email, sessionId), result.review);
      void client.invalidateQueries({ queryKey: REVIEW_EVENTS_KEY(email, sessionId) });
    },
    onError: (error) => {
      setNotice({ text: reviewErrorSentence(error), code: reviewRefusalOf(error) });
      // A refusal of the whole request says the page is out of date: read it again.
      void client.invalidateQueries({ queryKey: REVIEW_KEY(email, sessionId) });
    },
  });

  const canWrite = eligibility.canWrite;
  const { mutateAsync } = write;

  const apply = useCallback(
    async (operations: readonly ReviewOperation[]): Promise<boolean> => {
      if (!canWrite || write.isPending) return false;
      const body = reviewOperationsBodySchema.safeParse({ operations, via: null });
      if (!body.success) {
        setNotice({ text: REVIEW_FAILURE_SENTENCE, code: null });
        return false;
      }
      try {
        const result = await mutateAsync(body.data);
        const first = result.rejected[0];
        if (first === undefined) {
          setNotice(null);
          return true;
        }
        const more =
          result.rejected.length > 1 ? ` (${result.rejected.length} gestes refusés)` : '';
        setNotice({ text: `${REVIEW_REFUSAL_SENTENCE[first.code]}${more}`, code: first.code });
        return false;
      } catch {
        // `onError` of the mutation has said it.
        return false;
      }
    },
    [canWrite, mutateAsync, write.isPending],
  );

  const order = useMemo(
    () => grid?.objectives.map((objective) => objective.objectiveId) ?? [],
    [grid],
  );
  const api = useMemo(
    () => ({ sessionId, review, grid, order, canWrite, busy: write.isPending, apply }),
    [sessionId, review, grid, order, canWrite, write.isPending, apply],
  );

  useEffect(() => () => window.clearTimeout(flashTimer.current), []);

  // The arrival scrolls AFTER the render that lit the case, so the case it looks
  // for is on screen. `scrollIntoView` is optional-called: jsdom has none.
  useEffect(() => {
    if (flashCase === null) return;
    document
      .getElementById(`review-case-${flashCase}`)
      ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [flashCase]);

  const closePopover = useCallback(() => setPopover(null), []);

  const goToCase = (objectiveId: string): void => {
    setPopover(null);
    setFolded(false);
    setTab('rangement');
    setFlashCase(objectiveId);
    window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlashCase(null), FLASH_MS);
  };

  /** Ranges a passage in a case - from the list, from « Ranger aussi dans », or from a drop. */
  const range = async (objectiveId: string, target: PassageTarget): Promise<void> => {
    const applied = await apply([rangeOperation(objectiveId, target)]);
    setPopover(null);
    if (applied) {
      window.getSelection()?.removeAllRanges();
      setTab('rangement');
    }
  };

  const onSelect = (read: SelectionRead): void => {
    if (read.kind === 'refused') {
      setPopover({ kind: 'refusal', anchor: { rect: read.rect } });
    } else if (read.kind === 'reply') {
      // Without a grid there is no case to put it in: the page keeps the questions.
      if (grid === null) return;
      setPopover({
        kind: 'range',
        target: { turn: read.turn, ...read.passage },
        anchor: { rect: read.rect },
      });
    } else {
      setPopover({
        kind: 'annotate',
        turn: read.turn,
        passage: read.passage,
        anchor: { rect: read.rect },
      });
    }
  };

  const drop: CaseDrop = {
    active: dragging && canWrite && grid !== null,
    over,
    onOver: setOver,
    onDrop: (objectiveId) => {
      const target = dragged.current;
      dragged.current = null;
      setDragging(false);
      setOver(null);
      if (target !== null) void range(objectiveId, target);
    },
  };

  const protocol = detail.cascade.protocol;
  const protocolName = grid !== null && protocol?.id === grid.protocolId ? protocol.name : null;
  const protocolLabel = protocol === null ? null : `${protocol.name} v${protocol.versionNumber}`;

  /* ------------------------------------------------------------------ blocked */

  if (eligibility.reason === 'session-in-progress' || eligibility.reason === 'no-protocol-page') {
    return (
      <section
        data-testid={REVIEW_TEST_IDS.screen}
        data-state="blocked"
        className="flex flex-col gap-4"
      >
        <ReviewHead
          session={detail.session}
          exchanges={turns.length}
          blocked
          folded={false}
          onFold={() => undefined}
          showTrial={false}
          extractCount={0}
        />
        {banner}
        <p
          data-testid={REVIEW_TEST_IDS.blocked}
          data-reason={eligibility.reason}
          className="max-w-[75ch] rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-gray-700 text-sm"
        >
          {INELIGIBLE_SENTENCE[eligibility.reason]}{' '}
          <Link
            to="/admin/sessions/$sessionId"
            params={{ sessionId }}
            className="font-semibold text-bleu-nuit underline underline-offset-2"
          >
            Retour au parcours
          </Link>
        </p>
      </section>
    );
  }

  /* -------------------------------------------------------------------- ready */

  return (
    <ReviewProvider value={api}>
      <section
        data-testid={REVIEW_TEST_IDS.screen}
        data-state="ready"
        className="flex flex-col gap-4"
      >
        <ReviewHead
          session={detail.session}
          exchanges={turns.length}
          blocked={false}
          folded={folded}
          onFold={() => setFolded((was) => !was)}
          showTrial={grid !== null}
          extractCount={review.extracts.length}
        />
        {banner}
        {eligibility.reason === 'session-in-trash' ? (
          <p className="text-caption text-gray-600">{INELIGIBLE_SENTENCE['session-in-trash']}</p>
        ) : null}
        {notice === null ? null : (
          <p
            role="alert"
            data-testid={REVIEW_TEST_IDS.notice}
            data-code={notice.code ?? undefined}
            className="rounded-md bg-rouge-tint px-3 py-2 text-rouge-deep text-sm"
          >
            {notice.text}
          </p>
        )}

        <div
          className={cn(
            'grid items-start gap-6',
            folded ? 'grid-cols-1' : 'md:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]',
          )}
        >
          <div className="flex min-w-0 flex-col gap-2.5">
            <ReviewLegend />
            {turns.length === 0 ? (
              <p className="rounded-md border border-gray-200 px-4 py-6 text-gray-600 text-sm">
                Aucun échange : il n’y a rien à ranger ni à annoter. Le noyau et les notes restent
                ouverts.
              </p>
            ) : (
              <ReviewTranscript
                turns={turns}
                interviewer={interviewerName(presentation)}
                person={personFirstName(detail.session.personName?.value) ?? PERSON_SPEAKER}
                onSelect={onSelect}
                onDrag={(read) => {
                  if (read === null) {
                    dragged.current = null;
                    setDragging(false);
                    setOver(null);
                    return;
                  }
                  dragged.current = { turn: read.turn, ...read.passage };
                  setDragging(true);
                  setPopover(null);
                }}
                onOpenPlaced={(ids, anchor) => setPopover({ kind: 'placed', ids, anchor })}
                onAnnotate={(turn, anchor) =>
                  setPopover({ kind: 'annotate', turn, passage: null, anchor })
                }
              />
            )}
          </div>

          <ReviewPanel
            tab={tab}
            onTab={setTab}
            folded={folded}
            protocolName={protocolName}
            protocolLabel={protocolLabel}
            flashCase={flashCase}
            drop={drop}
            events={events.data?.events}
            eventsFailed={events.isError}
          />
        </div>

        {popover === null ? null : popover.kind === 'range' ? (
          <ReviewPopover
            kind="range"
            label="Ranger dans une case"
            anchor={popover.anchor}
            onClose={closePopover}
          >
            <ReviewRangeList
              target={popover.target}
              onPick={(objectiveId) => void range(objectiveId, popover.target)}
            />
          </ReviewPopover>
        ) : popover.kind === 'placed' ? (
          <ReviewPopover
            kind="placed"
            label="Passage rangé"
            anchor={popover.anchor}
            onClose={closePopover}
          >
            <ReviewPlacedList
              ids={popover.ids}
              onGoTo={goToCase}
              onPick={(objectiveId, target) => void range(objectiveId, target)}
              onEmpty={closePopover}
            />
          </ReviewPopover>
        ) : popover.kind === 'annotate' ? (
          <ReviewPopover
            kind="annotate"
            label="Annoter la question"
            anchor={popover.anchor}
            onClose={closePopover}
          >
            <ReviewAnnotateForm
              turn={popover.turn}
              passage={popover.passage}
              onDone={() => {
                window.getSelection()?.removeAllRanges();
                closePopover();
              }}
              onCancel={closePopover}
            />
          </ReviewPopover>
        ) : (
          <ReviewPopover
            kind="refusal"
            label="Sélection refusée"
            anchor={popover.anchor}
            onClose={closePopover}
          >
            <p role="alert" data-testid={REVIEW_TEST_IDS.refusal} className="text-rouge-deep">
              {SELECTION_REFUSAL}
            </p>
          </ReviewPopover>
        )}
      </section>
    </ReviewProvider>
  );
}
